import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLegalForm } from '../components/legal-form/useLegalForm';
import axios from 'axios';
import { API, useAuth } from '../App';
import { getCurrencySymbol as getCurrencySymbolUtil, formatCurrency } from '../utils/currency';
import { useNavigationContext } from '../hooks/useNavigationContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { CacheIndicator } from '../components/CacheIndicator';
import FreshnessChip from '../components/common/FreshnessChip';
import { 
  GLDrilldownModal,
  ProfitLossReport,
  BalanceSheetReport,
  CashFlowReport,
  TrialBalanceReport,
  AgedReceivablesReport,
  AgedPayablesReport,
  FXGainLossReport,
  CurrencySelector,
  AICommentary,
  ReportExportButton
} from '../components/reports';
import { WHTRemittanceReport } from '../components/reports/WHTRemittanceReport';
import { PendingScheduleWarning } from '../components/notifications/NotificationGovernance';
import CustomReportPreviewModal from '../components/reports/CustomReportPreviewModal';
import { 
  FileText, 
  TrendingUp, 
  TrendingDown,
  BarChart3,
  PieChart,
  ArrowLeft,
  Calendar,
  ChevronRight,
  Loader2,
  RefreshCw,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Download,
  Users,
  Building,
  Wallet,
  Clock,
  Trash2,
  Eye,
  FileBarChart,
  Search,
  BookOpen
} from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth, startOfYear, subDays } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { getCsrfToken } from '../utils/apiClient';
import { ProjectSelect } from '../components/projects/ProjectSelect';
import { ProjectColumnsPnl, projectColumns } from '../components/projects/ProjectColumnsPnl';
import { MultiColumnStatement } from '../components/reports/MultiColumnStatement';
import { StatementOfChangesInEquity } from '../components/reports/StatementOfChangesInEquity';
import { SalesTaxReturnForm } from '../components/reports/SalesTaxReturnForm';
import { normalizePnl, normalizeBalanceSheet, normalizeCashFlow } from '../utils/reportNormalize';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const ReportsPage = () => {
  const { data: legalFormInfo } = useLegalForm();
  const regimeLabels = legalFormInfo?.report_labels || {};
  const { business } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { navigateWithReturn } = useNavigationContext();
  const [activeReport, setActiveReport] = useState(null);

  // iter1077 - selectReport keeps activeReport and the URL ?report= param
  // in sync so the global PWA back button can leave the report view
  // (no more inline "<" back arrow needed on the report header).
  const selectReport = React.useCallback((reportId) => {
    setActiveReport(reportId);
    setReportData(null);
    setComparisonData(null);
    setAiCommentary(null);
    const next = new URLSearchParams(searchParams);
    if (reportId) {
      next.set('report', reportId);
    } else {
      next.delete('report');
    }
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  const [loading, setLoading] = useState(false);
  const reportBodyRef = useRef(null);
  const [reportData, setReportData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [reportFetchedAt, setReportFetchedAt] = useState(null);
  useEffect(() => { if (reportData) setReportFetchedAt(new Date().toISOString()); }, [reportData]);
  const [aiCommentary, setAiCommentary] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [viewMode, setViewMode] = useState('summary');
  
  // Custom reports state
  const [customReports, setCustomReports] = useState([]);
  const [loadingCustomReports, setLoadingCustomReports] = useState(false);
  const [selectedCustomReport, setSelectedCustomReport] = useState(null);
  
  // Trial Balance state
  const [showZeroBalances, setShowZeroBalances] = useState(false);
  const [trialBalanceFilter, setTrialBalanceFilter] = useState('all');
  const [trialBalanceViewMode, setTrialBalanceViewMode] = useState('summary'); // 'summary' or 'detailed'
  const [selectedGlAccount, setSelectedGlAccount] = useState(null);
  const [glTransactions, setGlTransactions] = useState(null);
  const [loadingGl, setLoadingGl] = useState(false);
  
  // GL Drilldown Modal state - now synced with URL for browser back button support
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownGlCode, setDrilldownGlCode] = useState(null);
  const [drilldownAccountId, setDrilldownAccountId] = useState(null);
  const [drilldownAccountName, setDrilldownAccountName] = useState(null);
  
  // GL Code visibility toggle (applies to all reports)
  const [showGlCodes, setShowGlCodes] = useState(true);
  
  // Period state
  const [period, setPeriod] = useState('this_month');
  const [comparePeriod, setComparePeriod] = useState('last_month');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  
  // Budget overlay (Phase 4 - Feb 2026)
  // When set, ReportsPage fetches the matching 3-statement budget's
  // cached statements and renders them alongside the actual report.
  const [availableBudgets, setAvailableBudgets] = useState([]);
  const [budgetCompareId, setBudgetCompareId] = useState(null);
  const [projectFilterId, setProjectFilterId] = useState(null);  // iter 1003: project dimension filter
  const [byProjectView, setByProjectView] = useState(false);  // iter 1004. P&L columns per project
  
  
  // Multi-currency display state
  const [displayCurrency, setDisplayCurrency] = useState(null);
  const [convertingCurrency, setConvertingCurrency] = useState(false);
  // Cash Flow method toggle - Indirect (IAS 7 default, most globally
  // accepted) vs Direct (totals only - no per-tx breakdown per user spec).
  const [cashFlowMethod, setCashFlowMethod] = useState('indirect');
  
  // Business class for dynamic labels (Cost of Goods Sold / Cost of Services)
  const [businessClassInfo, setBusinessClassInfo] = useState(null);

  // iter1374 - Closing-stock coverage for the selected period. Used to hint on
  // the P&L that COGS may be under/overstated when the periodic true-up is
  // pending for any month in the range.
  const [cogsCoverage, setCogsCoverage] = useState(null);

  // Fetch business class on mount
  useEffect(() => {
    const fetchBusinessClass = async () => {
      try {
        if (!getCsrfToken()) return;
        
        const res = await axios.get(`${API}/business/class`);
        setBusinessClassInfo(res.data);
      } catch (err) {
        console.log('Failed to fetch business class:', err);
      }
    };
    
    fetchBusinessClass();
  }, []);

  // iter1374 - Closing-stock coverage for the current report range. Only
  // relevant for reports that surface COGS (P&L today, plus its variants).
  // Silently skipped for orgs that don't track inventory.
  useEffect(() => {
    const fetchCoverage = async () => {
      if (!['pnl', 'income_statement'].includes(activeReport)) {
        setCogsCoverage(null);
        return;
      }
      const range = getDateRange(period);
      if (!range?.start || !range?.end) return;
      try {
        const r = await axios.get(
          `${API}/inventory/closing-stock/coverage?start_date=${range.start}&end_date=${range.end}`
        );
        setCogsCoverage(r.data);
      } catch {
        setCogsCoverage(null);
      }
    };
    if (getCsrfToken()) fetchCoverage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReport, period]);

  // Phase 4 - Load available 3-statement budgets that overlap the selected period.
  // iter 732: only "active" (non-archived) budgets whose period covers the
  // currently selected report window are surfaced.
  useEffect(() => {
    const loadBudgets = async () => {
      try {
        const range = getDateRange(period);
        if (!range?.start || !range?.end) return;
        const r = await axios.get(
          `${API}/budgets/v2/active-for-period?start_date=${range.start}&end_date=${range.end}`
        );
        setAvailableBudgets(r.data?.budgets || []);
      } catch (err) {
        // Silently skip - v2 endpoint may not be reachable for non-owners
        setAvailableBudgets([]);
      }
    };
    if (getCsrfToken()) loadBudgets();
  }, [period]);

  /**
   * URL-based drilldown state management for browser back button support.
   * When the drilldown modal opens, we update URL params.
   * When URL params change (e.g., browser back), we restore the modal.
   */
  
  // Read URL params and restore drilldown modal state on mount or URL change
  useEffect(() => {
    const urlGlCode = searchParams.get('drilldown_gl');
    const urlAccountId = searchParams.get('drilldown_id');
    const urlAccountName = searchParams.get('drilldown_name');
    const urlReport = searchParams.get('report');
    
    // Restore active report from URL (also handles: user hits global back
    // button → URL param cleared → we reset activeReport so the report
    // picker is shown again without needing a duplicate inline back arrow).
    if (urlReport && urlReport !== activeReport) {
      setActiveReport(urlReport);
    } else if (!urlReport && activeReport) {
      setActiveReport(null);
      setReportData(null);
      setComparisonData(null);
      setAiCommentary(null);
    }
    
    // Restore drilldown modal from URL params (browser back button scenario)
    if ((urlGlCode || urlAccountId) && !drilldownOpen) {
      setDrilldownGlCode(urlGlCode);
      setDrilldownAccountId(urlAccountId);
      setDrilldownAccountName(urlAccountName ? decodeURIComponent(urlAccountName) : null);
      setDrilldownOpen(true);
    }
    
    // Also check location.state as fallback (for in-app navigation)
    const stateGlCode = location.state?.drilldownGlCode;
    const stateAccountId = location.state?.drilldownAccountId;
    const stateAccountName = location.state?.drilldownAccountName;
    const stateReport = location.state?.activeReport;
    
    if (stateReport && !activeReport && !urlReport) {
      setActiveReport(stateReport);
    }
    
    if ((stateGlCode || stateAccountId) && !drilldownOpen && !urlGlCode && !urlAccountId) {
      setDrilldownGlCode(stateGlCode);
      setDrilldownAccountId(stateAccountId);
      setDrilldownAccountName(stateAccountName);
      setDrilldownOpen(true);
      
      // Sync to URL so browser back works even after in-app navigation
      updateDrilldownUrl(stateGlCode, stateAccountId, stateAccountName, stateReport || activeReport);
    }
  }, [searchParams, location.state]);

  // Update URL when drilldown modal opens/closes
  const updateDrilldownUrl = useCallback((glCode, accountId, accountName, report) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (glCode || accountId) {
      if (glCode) newParams.set('drilldown_gl', glCode);
      if (accountId) newParams.set('drilldown_id', accountId);
      if (accountName) newParams.set('drilldown_name', encodeURIComponent(accountName));
      if (report) newParams.set('report', report);
    }
    
    setSearchParams(newParams, { replace: false });
  }, [searchParams, setSearchParams]);

  // Clear drilldown URL params when modal closes
  const clearDrilldownUrl = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('drilldown_gl');
    newParams.delete('drilldown_id');
    newParams.delete('drilldown_name');
    // Keep report param so user stays on same report
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Handle drilldown modal open/close with URL sync
  const handleDrilldownOpenChange = useCallback((open) => {
    setDrilldownOpen(open);
    if (!open) {
      // Modal closing - clear URL params
      clearDrilldownUrl();
    }
  }, [clearDrilldownUrl]);

  // Fetch GL transactions for drill-down (Open Modal) - now updates URL
  const fetchGlTransactions = async (accountId, glCode = null, accountName = null) => {
    setDrilldownAccountId(accountId);
    setDrilldownGlCode(glCode);
    setDrilldownAccountName(accountName);
    setDrilldownOpen(true);
    
    // Update URL for browser back button support
    updateDrilldownUrl(glCode, accountId, accountName, activeReport);
  };
  
  // Open drill-down modal with GL code - also updates URL
  // Direct-method cash-flow line drill-down: journals behind one disclosure line.
  const fetchCashFlowLineItems = React.useCallback(async (section, line, method = 'direct') => {
    const range = getDateRange(period) || {};
    const params = new URLSearchParams(method === 'indirect' ? { line } : { section, line });
    const start = range.start_date || range.start; const end = range.end_date || range.end;
    if (start) params.set('start_date', start);
    if (end) params.set('end_date', end);
    if (displayCurrency) params.set('display_currency', displayCurrency);
    if (projectFilterId) params.set('project_id', projectFilterId);
    const path = method === 'indirect' ? 'cash-flow/indirect-line-items' : 'cash-flow/line-items';
    const res = await axios.get(`${API}/unified-reports/${path}?${params.toString()}`);
    return res.data;
  }, [period, displayCurrency, projectFilterId]);

  const openDrilldown = (item) => {
    const range = getDateRange(period) || {};
    const params = new URLSearchParams();
    if (range.start_date || range.start) params.set('start_date', range.start_date || range.start);
    if (range.end_date || range.end)     params.set('end_date',   range.end_date   || range.end);

    // Statement-of-Changes-in-Equity rows: drill into the *composite* set of
    // ledgers that make up the SoCiE line (e.g. "Net Profit" → all 4xxx-7xxx
    // P&L journals during the period). Goes to a dedicated breakdown page.
    if (item?.line_type) {
      params.set('line_type', item.line_type);
      const url = `/equity-breakdown/${encodeURIComponent(item.line_type)}?${params.toString()}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    const glCode = item.code || item.gl_code;
    if (!glCode) {
      toast.error('No GL code available for this row.');
      return;
    }
    // Phase C (Feb 2026) - drill-down now opens in a NEW TAB. Includes the
    // current report's date range so the breakdown matches what the user
    // saw on the report. Falls back to no date filter if no range is set.
    const qs = params.toString();
    const url = `/gl-breakdown/${encodeURIComponent(glCode)}${qs ? `?${qs}` : ''}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Legacy: Fetch GL transactions for inline card view (deprecated, keeping for backward compat)
  const fetchGlTransactionsInline = async (accountId) => {
    setLoadingGl(true);
    try {
      const dateRange = getDateRange(period);
      const response = await axios.get(`${API}/reports/gl-transactions/${accountId}?start_date=${dateRange.start}&end_date=${dateRange.end}`);
      setGlTransactions(response.data);
      setSelectedGlAccount(response.data.account);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoadingGl(false);
    }
  };

  // Download GL as CSV or PDF
  const downloadGl = async (accountId, format = 'csv') => {
    try {
      const dateRange = getDateRange(period);
      const response = await axios.get(
        `${API}/reports/gl-export/${accountId}?start_date=${dateRange.start}&end_date=${dateRange.end}&format=${format}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GL_${selectedGlAccount?.code || accountId}_${format}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  // Navigate to source transaction for editing with return context
  const navigateToSource = (transaction) => {
    // Check if this is an Open Banking transaction (read-only)
    if (transaction.is_open_banking || !transaction.can_edit) {
      toast.info('This transaction is from a bank sync connection and cannot be edited directly');
      return;
    }
    
    const sourceType = transaction.source_type;
    const sourceId = transaction.source_id || transaction.journal_entry_id;
    
    if (sourceType === 'invoice') {
      navigateWithReturn(`/invoices/${sourceId}/edit`);
    } else if (sourceType === 'bill') {
      navigateWithReturn(`/bills/${sourceId}/edit`);
    } else if (sourceType === 'journal_entry' || !sourceType) {
      navigateWithReturn(`/manual-journals?edit=${sourceId}`);
    } else {
      // Default to manual journals
      navigateWithReturn(`/manual-journals?edit=${sourceId}`);
    }
  };

  // Fetch custom reports on mount
  useEffect(() => {
    fetchCustomReports();
  }, []);

  const fetchCustomReports = async () => {
    setLoadingCustomReports(true);
    try {
      const response = await axios.get(`${API}/custom-reports`);
      setCustomReports(response.data);
    } catch (error) {
      console.error('Error fetching custom reports:', error);
    } finally {
      setLoadingCustomReports(false);
    }
  };

  const deleteCustomReport = async (reportId) => {
    try {
      await axios.delete(`${API}/custom-reports/${reportId}`);
      toast.success('Report deleted');
      fetchCustomReports();
      if (selectedCustomReport?.id === reportId) {
        setSelectedCustomReport(null);
      }
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const downloadCustomReport = async (report) => {
    try {
      const response = await axios.get(`${API}/custom-reports/${report.id}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Report downloaded as PDF');
    } catch (error) {
      // Fallback to text download if PDF fails
      const content = `${report.name}\nGenerated: ${new Date(report.created_at).toLocaleString()}\nType: ${report.report_type}\nQuery: ${report.query}\n\n${report.analysis}`.trim();
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name.replace(/\s+/g, '_')}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    }
  };

  const getCurrencySymbol = (currency = null) => {
    const currencyCode = currency || displayCurrency || getBaseCurrency();
    return getCurrencySymbolUtil(currencyCode);
  };

  const getBaseCurrency = () => {
    return business?.currency || 'NGN';
  };

  const formatAmount = (amount, currency = null) => {
    const currencyCode = currency || displayCurrency || getBaseCurrency();
    return formatCurrency(amount, currencyCode);
  };

  // Refetch Cash Flow when the user toggles between Indirect / Direct.
  useEffect(() => {
    if (activeReport === 'cash_flow') {
      fetchReport('cash_flow');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashFlowMethod]);

  // Reset display currency when changing reports
  useEffect(() => {
    setDisplayCurrency(null);
  }, [activeReport]);

  const allReports = [
    { id: 'pnl', name: regimeLabels.pnl_title || 'Profit & Loss', businessName: regimeLabels.pnl_title === 'Income & Expenditure' ? 'Income & Expenditure' : 'Income Summary', description: 'See your revenue, expenses, and whether you made a profit', icon: BarChart3, color: '#283F8B', category: 'Financial Statements' },
    { id: 'balance_sheet', name: regimeLabels.bs_title || 'Statement of Financial Position', businessName: 'What You Own & Owe', description: 'A snapshot of what your business owns, owes, and is worth', icon: PieChart, color: '#108981', category: 'Financial Statements' },
    { id: 'cash_flow', name: 'Cash Flow Statement', businessName: 'Cash Movement', description: 'See where your cash came from and where it went', icon: TrendingUp, color: '#5FC6C5', category: 'Financial Statements' },
    { id: 'equity', name: 'Statement of Changes in Equity', businessName: 'Ownership Changes', description: 'Track changes in what the business is worth to its owners', icon: Wallet, color: '#283F8B', category: 'Financial Statements' },
    { id: 'trial_balance', name: 'Trial Balance', description: 'All GL accounts with debits and credits - click to drill down', icon: FileBarChart, color: '#7C3AED', category: 'Financial Statements' },
    { id: 'tax_report', name: (String(business?.country||'').toUpperCase()==='US' ? 'Sales Tax Report' : 'VAT Report'), businessName: (String(business?.country||'').toUpperCase()==='US' ? 'Sales Tax Summary' : 'VAT Summary'), description: (String(business?.country||'').toUpperCase()==='US' ? 'Sales tax collected from customers and what you owe your state DOR' : 'VAT collected on sales, VAT paid on purchases, and what you owe'), icon: FileText, color: '#DC2626', category: 'Tax Reports' },
    { id: 'vat_return', name: (String(business?.country||'').toUpperCase()==='US' ? 'Sales Tax Return' : 'VAT Return'), description: (String(business?.country||'').toUpperCase()==='US' ? 'Per-state sales tax collected, taxable / exempt breakdown - use before filing with your state' : 'Box-by-box VAT return data for your tax authority'), icon: FileBarChart, color: '#EA580C', category: 'Tax Reports' },
    { id: 'wht_remittance', name: 'WHT Remittance Report', businessName: 'Withholding Tax', description: 'Tax deducted at source, ready to file with your tax authority', icon: Building, color: '#F59E0B', category: 'Tax Reports' },
    { id: 'tax_remittance', name: 'Compliance Remittance Management', businessName: 'Compliance Remittance Management', description: (String(business?.country||'').toUpperCase()==='US' ? 'Sales Tax, PAYE and other statutory remittances' : 'VAT, WHT, PAYE, Pension and other statutory remittances'), icon: FileText, color: '#8B5CF6', category: 'Tax Reports' },
    { id: 'fx_gainloss', name: 'FX Gain/Loss Report', businessName: 'Currency Gains/Losses', description: 'Gains and losses from foreign currency transactions', icon: TrendingUp, color: '#10B981', category: 'Management Reports' },
    { id: 'aged_receivables', name: 'Aged Receivables', businessName: 'Money Owed to You', description: 'See who owes you money and how long it\'s been outstanding', icon: Users, color: '#108981', category: 'Management Reports' },
    { id: 'aged_payables', name: 'Aged Payables', businessName: 'Money You Owe', description: 'See who you owe money to and when it\'s due', icon: Building, color: '#5FC6C5', category: 'Management Reports' },
  ];

  // All reports visible to all users
  const reports = allReports.map(report => ({
    ...report,
    name: report.name,
  }));

  const periodOptions = [
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'ytd', label: 'Year to Date' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const getDateRange = (periodValue) => {
    const now = new Date();
    switch (periodValue) {
      case 'this_month':
        return { start: format(startOfMonth(now), 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return { start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'), end: format(endOfMonth(lastMonth), 'yyyy-MM-dd') };
      case 'this_quarter':
        const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        return { start: format(qStart, 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
      case 'last_quarter':
        const lqEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 0);
        const lqStart = new Date(lqEnd.getFullYear(), lqEnd.getMonth() - 2, 1);
        return { start: format(lqStart, 'yyyy-MM-dd'), end: format(lqEnd, 'yyyy-MM-dd') };
      case 'ytd':
        return { start: format(startOfYear(now), 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
      case 'last_year':
        const lastYear = now.getFullYear() - 1;
        return { start: `${lastYear}-01-01`, end: `${lastYear}-12-31` };
      case 'custom':
        return { 
          start: customStart ? format(customStart, 'yyyy-MM-dd') : format(startOfMonth(now), 'yyyy-MM-dd'),
          end: customEnd ? format(customEnd, 'yyyy-MM-dd') : format(now, 'yyyy-MM-dd')
        };
      default:
        return { start: format(startOfMonth(now), 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
    }
  };

  // Comparison choices: single periods that differ from the report's own range, plus monthly trends.
  const TREND_OPTIONS = [
    { value: 'trend_3', label: 'Last 3 months (trend)', months: 3 },
    { value: 'trend_6', label: 'Last 6 months (trend)', months: 6 },
    { value: 'trend_12', label: 'Last 12 months (trend)', months: 12 },
  ];
  const isTrend = comparePeriod.startsWith('trend_');
  const trendMonths = TREND_OPTIONS.find((o) => o.value === comparePeriod)?.months || 0;
  const currentRange = getDateRange(period);
  const compareOptions = [
    ...periodOptions.filter((p) => {
      if (p.value === 'custom') return false;
      const r = getDateRange(p.value);
      return !(r.start === currentRange.start && r.end === currentRange.end);
    }),
    ...TREND_OPTIONS,
  ];
  const compareOptionLabel = compareOptions.find((o) => o.value === comparePeriod)?.label || 'Previous period';

  // Month windows for a trend, ending with the month of the report's end date.
  const trendWindows = (n, endDateStr) => {
    const end = new Date(endDateStr);
    return Array.from({ length: n }, (_, i) => {
      const m = subMonths(startOfMonth(end), n - 1 - i);
      const last = endOfMonth(m) > end ? end : endOfMonth(m);
      return { key: format(m, 'yyyy-MM'), label: format(m, 'MMM yyyy'), start: format(m, 'yyyy-MM-dd'), end: format(last, 'yyyy-MM-dd') };
    });
  };

  // Latest month vs the average of the earlier months, for the on-demand AI analysis of a trend.
  const trendAiPair = (months) => {
    if (!months || months.length < 2) return null;
    const latest = months[months.length - 1].data;
    const earlier = months.slice(0, -1).map((m) => m.data);
    const avg = (pick) => earlier.reduce((s, d) => s + (Number(pick(d)) || 0), 0) / earlier.length;
    const avgAccounts = (pick) => {
      const acc = new Map();
      earlier.forEach((d) => (pick(d) || []).forEach((a) => {
        const k = a.code || a.name; const cur = acc.get(k) || { code: a.code, name: a.name, amount: 0, balance: 0 };
        cur.amount += (a.amount || 0) / earlier.length; cur.balance += (a.balance || 0) / earlier.length; acc.set(k, cur);
      }));
      return Array.from(acc.values());
    };
    const summary = Object.fromEntries(Object.keys(latest.summary || {}).map((k) => [k, avg((d) => d.summary?.[k])]));
    const comparison = { ...latest, summary, source: 'trend_average' };
    ['revenue', 'gross_revenue', 'discount_allowed', 'cost_of_sales', 'operating_expenses', 'other_income', 'other_expenses', 'income_tax'].forEach((k) => {
      if (Array.isArray(latest[k])) comparison[k] = avgAccounts((d) => d[k]);
    });
    if (latest.assets) {
      comparison.assets = { total: avg((d) => d.assets?.total), current: { items: avgAccounts((d) => d.assets?.current?.items), total: avg((d) => d.assets?.current?.total) }, non_current: { items: avgAccounts((d) => d.assets?.non_current?.items), total: avg((d) => d.assets?.non_current?.total) } };
      comparison.liabilities = { total: avg((d) => d.liabilities?.total), current: { items: avgAccounts((d) => d.liabilities?.current?.items), total: avg((d) => d.liabilities?.current?.total) }, non_current: { items: avgAccounts((d) => d.liabilities?.non_current?.items), total: avg((d) => d.liabilities?.non_current?.total) } };
      comparison.equity = { ...latest.equity, total: avg((d) => d.equity?.total), items: avgAccounts((d) => d.equity?.items) };
    }
    if (latest.operating) {
      comparison.operating = { net: avg((d) => d.operating?.net) }; comparison.investing = { net: avg((d) => d.investing?.net) }; comparison.financing = { net: avg((d) => d.financing?.net) };
      comparison.net_change = avg((d) => d.net_change);
    }
    return { current: latest, comparison };
  };


  const fetchReport = async (reportId, currencyOverride, projectOverride) => {
    setLoading(true);
    setAiCommentary(null);
    setTrendData(null);
    const dateRange = getDateRange(period);
    // iter 1003 - project filter (same override pattern as currency)
    const effProject = projectOverride !== undefined ? projectOverride : projectFilterId;
    const projectParam = effProject ? `&project_id=${effProject}` : '';
    
    // Build currency parameter for multi-currency support.
    // We accept an explicit override so callers (e.g. the currency selector's
    // onChange) can refetch with the NEW value without waiting for React state
    // to flush. `null` means "switch back to base" → no query param.
    const effectiveDisplayCurrency = currencyOverride !== undefined ? currencyOverride : displayCurrency;
    const currencyParam = effectiveDisplayCurrency ? `&display_currency=${effectiveDisplayCurrency}` : '';
    
    try {
      let url = '';
      let dataSet = false;
      
      // UNIFIED REPORTS: All reports now use unified-reports endpoints (Single Source of Truth - journals_unified)
      
      if (reportId === 'pnl') {
        // UNIFIED: P&L from journals_unified
        const budgetParam = budgetCompareId ? `&budget_id=${budgetCompareId}` : '';
        const unifiedUrl = `${API}/unified-reports/profit-loss?start_date=${dateRange.start}&end_date=${dateRange.end}${currencyParam}${budgetParam}${projectParam}`;
        const response = await axios.get(unifiedUrl);
        const data = response.data;
        
        // Map accounts with proper amount field for consistency
        const mapAccounts = (accounts) => (accounts || []).map(a => ({
          code: a.code,
          name: a.name,
          amount: a.amount || a.balance || 0
        }));
        
        setReportData({
          summary: {
            total_revenue: data.revenue?.total || 0,
            gross_revenue: data.gross_revenue?.total || data.revenue?.total || 0,
            discount_allowed: data.contra_revenue?.total || 0,
            total_cost_of_sales: data.cost_of_sales?.total || 0,
            gross_profit: data.gross_profit || 0,
            total_operating_expenses: data.operating_expenses?.total || 0,
            operating_profit: data.operating_profit || 0,
            total_other_income: data.other_operating_income?.total || 0,
            total_other_expenses: data.other_expenses?.total || 0,
            profit_before_tax: data.profit_before_tax ?? data.net_profit ?? 0,
            total_income_tax: data.income_tax_expense?.total || 0,
            net_profit: data.net_profit || 0,
            profit_margin: data.net_margin_pct || 0
          },
          // Gross Revenue accounts (regular revenue GL codes)
          gross_revenue: mapAccounts(data.gross_revenue?.accounts || data.revenue?.accounts),
          // Discount Allowed (contra-revenue - Sales Discounts, Sales Returns)
          discount_allowed: mapAccounts(data.contra_revenue?.accounts || []),
          // Net Revenue (kept for backward compatibility)
          revenue: mapAccounts(data.revenue?.accounts),
          cost_of_sales: mapAccounts(data.cost_of_sales?.accounts),
          operating_expenses: mapAccounts(data.operating_expenses?.accounts),
          other_income: mapAccounts(data.other_operating_income?.accounts),
          other_expenses: mapAccounts(data.other_expenses?.accounts),
          income_tax: mapAccounts(data.income_tax_expense?.accounts),
          period: data.period,
          // Use display_currency if multi-currency, otherwise original currency
          currency: data.display_currency || data.currency,
          original_currency: data.original_currency || data.currency,
          fx_rate: data.fx_rate,
          // Phase 4 - budget overlay payload (when budgetCompareId is set)
          budget: data.budget || null,
          source: 'journals_unified'
        });
        dataSet = true;
      } else if (reportId === 'balance_sheet') {
        // UNIFIED: Balance Sheet from journals_unified
        const budgetParam = budgetCompareId ? `&budget_id=${budgetCompareId}` : '';
        const unifiedUrl = `${API}/unified-reports/balance-sheet?as_of_date=${dateRange.end}${currencyParam}${budgetParam}${projectParam}`;
        const response = await axios.get(unifiedUrl);
        const data = response.data;
        
        // Transform to expected structure with proper balance field
        const mapItems = (items) => (items || []).map(a => ({ 
          code: a.code, 
          name: a.name, 
          balance: a.balance || 0 
        }));
        
        const currentAssets = mapItems(data.assets?.current);
        const nonCurrentAssets = mapItems(data.assets?.non_current);
        const currentLiabilities = mapItems(data.liabilities?.current);
        const nonCurrentLiabilities = mapItems(data.liabilities?.non_current);
        const equityItems = mapItems(data.equity);
        
        setReportData({
          assets: {
            total: data.total_assets || 0,
            current: {
              items: currentAssets,
              total: currentAssets.reduce((sum, a) => sum + (a.balance || 0), 0)
            },
            non_current: {
              items: nonCurrentAssets,
              total: nonCurrentAssets.reduce((sum, a) => sum + (a.balance || 0), 0)
            }
          },
          liabilities: {
            total: data.total_liabilities || 0,
            current: {
              items: currentLiabilities,
              total: currentLiabilities.reduce((sum, l) => sum + (l.balance || 0), 0)
            },
            non_current: {
              items: nonCurrentLiabilities,
              total: nonCurrentLiabilities.reduce((sum, l) => sum + (l.balance || 0), 0)
            }
          },
          equity: {
            total: data.total_equity || 0,
            items: equityItems,
            retained_earnings_current_year: data.retained_earnings_current_year || 0,
            // Equity breakdown summary
            summary: data.equity_summary || {
              share_capital: 0,
              accumulated_retained_earnings: 0,
              current_year_earnings: data.retained_earnings_current_year || 0,
              total_retained_earnings: data.retained_earnings_current_year || 0,
              other_equity: 0
            }
          },
          equity_summary: data.equity_summary,
          is_balanced: data.is_balanced,
          as_of_date: data.as_of_date,
          // Use display_currency if multi-currency
          currency: data.display_currency || data.currency,
          original_currency: data.original_currency || data.currency,
          fx_rate: data.fx_rate,
          // Phase 4 - budget overlay payload
          budget: data.budget || null,
          source: 'journals_unified'
        });
        dataSet = true;
      } else if (reportId === 'cash_flow') {
        // UNIFIED: Cash Flow from journals_unified.
        // Phase 1-4 (Feb 2026) - Indirect method is now the default; Direct
        // is available via the toggle (cashFlowMethod state).
        const method = (typeof cashFlowMethod === 'string' && cashFlowMethod) || 'indirect';
        const budgetParam = budgetCompareId ? `&budget_id=${budgetCompareId}` : '';
        const unifiedUrl = `${API}/unified-reports/cash-flow?method=${method}&start_date=${dateRange.start}&end_date=${dateRange.end}${currencyParam}${budgetParam}${projectParam}`;
        const response = await axios.get(unifiedUrl);
        const cfData = response.data;
        setReportData(normalizeCashFlow(cfData, method));
        dataSet = true;
      } else if (reportId === 'equity') {
        // UNIFIED: Statement of Changes in Equity from journals_unified
        const unifiedUrl = `${API}/unified-reports/changes-in-equity?start_date=${dateRange.start}&end_date=${dateRange.end}${currencyParam}`;
        const response = await axios.get(unifiedUrl);
        // Backend now returns the full multi-column statement (rows/components/accounts/movements/reconciliation).
        setReportData(response.data);
        dataSet = true;
      } else if (reportId === 'trial_balance') {
        // UNIFIED: Trial Balance from journals_unified
        const unifiedUrl = `${API}/unified-reports/trial-balance?start_date=${dateRange.start}&end_date=${dateRange.end}${currencyParam}`;
        const response = await axios.get(unifiedUrl);
        const data = response.data;
        setReportData({
          detailed: {
            accounts: data.accounts || [],
            total_debits: data.total_debits || 0,
            total_credits: data.total_credits || 0,
            is_balanced: data.is_balanced,
            period: data.period
          },
          summary: {
            assets: data.accounts?.filter(a => a.code?.startsWith('1')).reduce((sum, a) => sum + (a.debit || 0), 0) || 0,
            liabilities: data.accounts?.filter(a => a.code?.startsWith('2')).reduce((sum, a) => sum + (a.credit || 0), 0) || 0,
            equity: data.accounts?.filter(a => a.code?.startsWith('3')).reduce((sum, a) => sum + (a.credit || 0), 0) || 0,
            revenue: data.accounts?.filter(a => a.code?.startsWith('4')).reduce((sum, a) => sum + (a.credit || 0), 0) || 0,
            expenses: data.accounts?.filter(a => a.code?.startsWith('5') || a.code?.startsWith('6')).reduce((sum, a) => sum + (a.debit || 0), 0) || 0
          },
          source: 'journals_unified'
        });
        setLoading(false);
        return;
      } else if (reportId === 'tax_report') {
        // UNIFIED: VAT Report from journals_unified
        // Shows Output VAT (sales) and Input VAT (purchases) - WHT is in separate report
        const unifiedUrl = `${API}/unified-reports/tax-report?start_date=${dateRange.start}&end_date=${dateRange.end}${currencyParam}`;
        const response = await axios.get(unifiedUrl);
        const taxData = response.data;
        
        // Output VAT = credits to 2200 (VAT collected on sales)
        const outputVatTotal = taxData.vat?.collected?.total || 0;
        const outputVatReversals = taxData.vat?.collected?.reversals_total || 0;
        const netOutputVat = taxData.vat?.collected?.net || (outputVatTotal - outputVatReversals);
        
        // Input VAT = debits to 2201 (VAT paid on purchases)
        const inputVatTotal = taxData.vat?.refunds?.total || 0;
        const inputVatReversals = taxData.vat?.refunds?.reversals_total || 0;
        const netInputVat = taxData.vat?.refunds?.net || (inputVatTotal - inputVatReversals);
        
        // Net VAT Payable
        const netVatPayable = taxData.vat?.net_payable || (netOutputVat - netInputVat);
        
        // Get Output VAT transaction breakdowns (VAT on sales/invoices)
        const outputTaxTransactions = (taxData.vat?.collected?.transactions || []).map(t => ({
          date: t.date,
          description: t.description,
          reference: t.reference,
          source_type: t.source_type,
          account_code: t.account_code || '2200',
          account_name: t.account_name || 'VAT Output Payable',
          amount: t.amount || t.credit || 0,
          sales_amount: t.sales_amount || 0,
          type: 'output'
        }));
        
        // Get Input VAT transaction breakdowns (VAT on purchases/bills)
        const inputTaxTransactions = (taxData.vat?.refunds?.transactions || []).map(t => ({
          date: t.date,
          description: t.description,
          reference: t.reference,
          source_type: t.source_type,
          account_code: t.account_code || '2201',
          account_name: t.account_name || 'VAT Input Receivable',
          amount: t.amount || t.debit || 0,
          purchase_amount: t.purchase_amount || 0,
          type: 'input'
        }));
        
        // Transform to expected format for VAT Report component
        setReportData({
          source: 'journals_unified',
          period: taxData.period,
          currency: taxData.display_currency || taxData.currency,
          country: 'Nigeria',
          // Summary cards
          tax_collected: {
            total: netOutputVat,
            gross: outputVatTotal,
            reversals: outputVatReversals,
            items: netOutputVat > 0 ? [{ name: 'VAT Output (Sales)', code: '2200', amount: netOutputVat }] : []
          },
          tax_paid: {
            total: netInputVat,
            amount: netInputVat,
            gross: inputVatTotal,
            reversals: inputVatReversals,
            items: netInputVat > 0 ? [{ name: 'VAT Input (Purchases)', code: '2201', amount: netInputVat }] : []
          },
          net_tax_liability: netVatPayable,
          // Output Tax transactions breakdown
          output_tax: {
            total: netOutputVat,
            gross: outputVatTotal,
            count: outputTaxTransactions.length,
            transactions: outputTaxTransactions,
            reversals: taxData.vat?.collected?.reversals || []
          },
          // Input Tax transactions breakdown
          input_tax: {
            total: netInputVat,
            gross: inputVatTotal,
            count: inputTaxTransactions.length,
            transactions: inputTaxTransactions,
            reversals: taxData.vat?.refunds?.reversals || []
          },
          // All VAT transactions combined
          vat: {
            output: netOutputVat,
            input: netInputVat,
            net: netVatPayable,
            transactions: [...outputTaxTransactions, ...inputTaxTransactions]
          },
          // Summary totals
          summary: {
            total_output_tax: netOutputVat,
            total_input_tax: netInputVat,
            net_vat_payable: netVatPayable
          }
        });
        dataSet = true;
      } else if (reportId === 'vat_return') {
        // UNIFIED: VAT Return from journals_unified
        const unifiedUrl = `${API}/unified-reports/vat-return?start_date=${dateRange.start}&end_date=${dateRange.end}${currencyParam}`;
        const response = await axios.get(unifiedUrl);
        const vatData = response.data;
        
        // Extract values from the unified API response
        const boxes = vatData.boxes || {};
        const summary = vatData.summary || {};
        const vatOutput = summary.output_vat || boxes.box1_vat_due_sales || 0;
        const vatInput = summary.input_vat || boxes.box4_vat_reclaimed || 0;
        const netVat = summary.net_vat_payable || boxes.box5_net_vat || 0;
        
        // Transform to expected format (matching template box-by-box structure)
        setReportData({
          source: 'journals_unified',
          period: vatData.period,
          currency: vatData.display_currency || vatData.currency,
          format: vatData.format || 'Standard VAT Return',
          tax_rate: vatData.vat_rate ?? 0,
          return_form: vatData.return_form || null,
          rates: vatData.rates || null,
          country: vatData.country,
          // Summary for cards
          summary: {
            vat_payable: netVat > 0 ? netVat : 0,
            vat_refund: netVat < 0 ? Math.abs(netVat) : 0,
            box1_output: vatOutput,
            box2_input: vatInput,
            box3_net: netVat,
            transaction_count: summary.transaction_count || 0
          },
          // Box-by-box format for template
          vat_return: {
            box1_vat_due_sales: boxes.box1_vat_due_sales || vatOutput,
            box2_vat_due_ec_acquisitions: boxes.box2_vat_due_acquisitions || 0,
            box3_total_vat_due: boxes.box3_total_vat_due || vatOutput,
            box4_vat_reclaimed_inputs: boxes.box4_vat_reclaimed || vatInput,
            box5_net_vat: boxes.box5_net_vat || netVat,
            box6_total_sales_ex_vat: boxes.box6_total_sales || 0,
            box7_total_purchases_ex_vat: boxes.box7_total_purchases || 0,
            box8_total_ec_supplies: boxes.box8_supplies_ec || 0,
            box9_total_ec_acquisitions: boxes.box9_acquisitions_ec || 0
          },
          // Output VAT (collected on sales)
          output_vat: {
            total: vatOutput,
            transactions: vatData.transactions || []
          },
          // Input VAT (paid on purchases)
          input_vat: {
            total: vatInput,
            transactions: []  // Input VAT refunds would be separate if tracked
          },
          // Net VAT
          net_vat: netVat
        });
        dataSet = true;
      } else if (reportId === 'aged_receivables') {
        // UNIFIED: Aged Receivables from journals_unified
        url = `${API}/unified-reports/aged-receivables?as_of_date=${dateRange.end}${currencyParam}`;
      } else if (reportId === 'aged_payables') {
        // UNIFIED: Aged Payables from journals_unified
        url = `${API}/unified-reports/aged-payables?as_of_date=${dateRange.end}${currencyParam}`;
      } else if (reportId === 'fx_gainloss') {
        // UNIFIED: FX Gain/Loss from journals_unified
        url = `${API}/unified-reports/fx-gain-loss?start_date=${dateRange.start}&end_date=${dateRange.end}${currencyParam}`;
      } else if (reportId === 'wht_remittance') {
        // UNIFIED: WHT Remittance from journals_unified
        url = `${API}/unified-reports/wht-remittance?start_date=${dateRange.start}&end_date=${dateRange.end}${currencyParam}`;
      } else if (reportId === 'tax_remittance') {
        // Navigate to Tax Remittance page - it's a full page report, not inline
        navigate('/tax-remittance');
        setLoading(false);
        return;
      }
      
      if (url && !dataSet) {
        const response = await axios.get(url);
        setReportData({ ...response.data, source: 'journals_unified' });
      }
      
      // Fetch comparison data - period OR budget (mutually exclusive).
      // Budget comparison takes priority when a budget is selected:
      // we shape the budget's cached statements into the SAME
      // `comparisonData` payload as a period comparison so the existing
      // line-by-line columns in ProfitLossReport / BalanceSheetReport /
      // CashFlowReport render with zero divergence.
      const shouldCompareBudget = !!budgetCompareId && ['pnl', 'cash_flow', 'balance_sheet'].includes(reportId);
      if (shouldCompareBudget) {
        // Auto-enable the comparison toggle so the report shows extra columns.
        if (!showComparison) setShowComparison(true);
        // iter 732 - read from the freshly fetched response, not stale state.
        // The main fetch already attached `data.budget` via ?budget_id=
        // because we re-call ?budget_id= here for safety in case the
        // initial setReportData hadn't re-rendered.
        let bData = null;
        try {
          let refUrl = '';
          if (reportId === 'pnl') {
            refUrl = `${API}/unified-reports/profit-loss?start_date=${dateRange.start}&end_date=${dateRange.end}${currencyParam}&budget_id=${budgetCompareId}`;
          } else if (reportId === 'balance_sheet') {
            refUrl = `${API}/unified-reports/balance-sheet?as_of_date=${dateRange.end}${currencyParam}&budget_id=${budgetCompareId}`;
          } else if (reportId === 'cash_flow') {
            const method = (typeof cashFlowMethod === 'string' && cashFlowMethod) || 'indirect';
            refUrl = `${API}/unified-reports/cash-flow?method=${method}&start_date=${dateRange.start}&end_date=${dateRange.end}${currencyParam}&budget_id=${budgetCompareId}`;
          }
          if (refUrl) {
            const refResp = await axios.get(refUrl);
            bData = refResp.data?.budget || null;
          }
        } catch (e) {
          // ignore - period comparison path will still work
        }
        if (bData) {
          if (reportId === 'pnl' && bData.profit_loss) {
            const p = bData.profit_loss;
            const mapAccounts = (accounts) => (accounts || []).map(a => ({
              code: a.code, name: a.name, amount: a.amount || a.balance || 0
            }));
            setComparisonData({
              summary: {
                total_revenue: p.revenue?.total || 0,
                gross_revenue: p.gross_revenue?.total || p.revenue?.total || 0,
                discount_allowed: p.contra_revenue?.total || 0,
                total_cost_of_sales: p.cost_of_sales?.total || 0,
                gross_profit: p.gross_profit || 0,
                total_operating_expenses: p.operating_expenses?.total || 0,
                total_other_expenses: p.other_expense?.total || 0,
                net_profit: p.net_profit || 0,
                profit_margin: p.net_margin_pct || 0,
              },
              gross_revenue: mapAccounts(p.gross_revenue?.accounts || p.revenue?.accounts),
              discount_allowed: mapAccounts(p.contra_revenue?.accounts || []),
              revenue: mapAccounts(p.revenue?.accounts),
              cost_of_sales: mapAccounts(p.cost_of_sales?.accounts),
              operating_expenses: mapAccounts(p.operating_expenses?.accounts),
              other_expenses: mapAccounts(p.other_expense?.accounts),
              period: p.period,
              source: 'budget_overlay',
              budget_meta: { name: bData.budget_name, scenario: bData.scenario, year: bData.year },
            });
          } else if (reportId === 'balance_sheet' && bData.balance_sheet) {
            const b = bData.balance_sheet;
            const mapItems = (items) => (items || []).map(a => ({ code: a.code, name: a.name, balance: a.balance || 0 }));
            setComparisonData({
              assets: {
                total: b.total_assets || 0,
                current: { items: mapItems(b.assets?.current?.items), total: b.assets?.current?.total || 0 },
                non_current: { items: mapItems(b.assets?.non_current?.items), total: b.assets?.non_current?.total || 0 },
              },
              liabilities: {
                total: b.total_liabilities || 0,
                current: { items: mapItems(b.liabilities?.current?.items), total: b.liabilities?.current?.total || 0 },
                non_current: { items: mapItems(b.liabilities?.non_current?.items), total: b.liabilities?.non_current?.total || 0 },
              },
              equity: { total: b.total_equity || 0, items: mapItems(b.equity?.items) },
              as_of_date: b.period?.as_of,
              source: 'budget_overlay',
              budget_meta: { name: bData.budget_name, scenario: bData.scenario, year: bData.year },
            });
          } else if (reportId === 'cash_flow' && (bData.cash_flow_indirect || bData.cash_flow_direct)) {
            const c = bData.cash_flow_indirect || bData.cash_flow_direct;
            const opNet = c.operating_activities?.net_cash ?? 0;
            const invNet = c.investing_activities?.net_cash ?? 0;
            const finNet = c.financing_activities?.net_cash ?? 0;
            const netCh = c.net_change_in_cash ?? (opNet + invNet + finNet);
            setComparisonData({
              operating: { net: opNet },
              investing: { net: invNet },
              financing: { net: finNet },
              net_change: netCh,
              beginning_cash: c.opening_cash || 0,
              ending_cash: c.closing_cash || 0,
              summary: { opening_cash: c.opening_cash || 0, net_change: netCh, closing_cash: c.closing_cash || 0 },
              source: 'budget_overlay',
              budget_meta: { name: bData.budget_name, scenario: bData.scenario, year: bData.year },
            });
          }
        }
      } else if (showComparison && isTrend && ['pnl', 'cash_flow', 'balance_sheet'].includes(reportId)) {
        // Monthly trend: one full statement per month, rendered side by side.
        const windows = trendWindows(trendMonths, dateRange.end);
        const urlFor = (w) => {
          if (reportId === 'pnl') return `${API}/unified-reports/profit-loss?start_date=${w.start}&end_date=${w.end}${currencyParam}${projectParam}`;
          if (reportId === 'balance_sheet') return `${API}/unified-reports/balance-sheet?as_of_date=${w.end}${currencyParam}${projectParam}`;
          const method = (typeof cashFlowMethod === 'string' && cashFlowMethod) || 'indirect';
          return `${API}/unified-reports/cash-flow?method=${method}&start_date=${w.start}&end_date=${w.end}${currencyParam}${projectParam}`;
        };
        const normalize = reportId === 'pnl' ? normalizePnl : reportId === 'balance_sheet' ? normalizeBalanceSheet : normalizeCashFlow;
        const responses = await Promise.all(windows.map((w) => axios.get(urlFor(w))));
        setComparisonData(null);
        setTrendData({
          months: windows.map((w, i) => ({
            key: w.key, label: w.label,
            sublabel: reportId === 'balance_sheet' ? `as at ${w.end}` : `${w.start} → ${w.end}`,
            data: normalize(responses[i].data),
          })),
        });
      } else if (showComparison && ['pnl', 'cash_flow', 'balance_sheet', 'trial_balance'].includes(reportId)) {
        const compareDateRange = getDateRange(comparePeriod);
        let compareUrl = '';
        
        if (reportId === 'pnl') {
          compareUrl = `${API}/unified-reports/profit-loss?start_date=${compareDateRange.start}&end_date=${compareDateRange.end}${currencyParam}`;
        } else if (reportId === 'cash_flow') {
          compareUrl = `${API}/unified-reports/cash-flow?method=${(typeof cashFlowMethod === 'string' && cashFlowMethod) || 'indirect'}&start_date=${compareDateRange.start}&end_date=${compareDateRange.end}${currencyParam}${projectParam}`;
        } else if (reportId === 'balance_sheet') {
          compareUrl = `${API}/unified-reports/balance-sheet?as_of_date=${compareDateRange.end}${currencyParam}`;
        } else if (reportId === 'trial_balance') {
          compareUrl = `${API}/unified-reports/trial-balance?end_date=${compareDateRange.end}${currencyParam}`;
        }
        
        if (compareUrl) {
          const compareResponse = await axios.get(compareUrl);
          const cData = compareResponse.data;
          
          // Apply the SAME transformation as main reportData so comparison fields match
          if (reportId === 'pnl') {
            const mapAccounts = (accounts) => (accounts || []).map(a => ({
              code: a.code, name: a.name, amount: a.amount || a.balance || 0
            }));
            setComparisonData({
              summary: {
                total_revenue: cData.revenue?.total || 0,
                gross_revenue: cData.gross_revenue?.total || cData.revenue?.total || 0,
                discount_allowed: cData.contra_revenue?.total || 0,
                total_cost_of_sales: cData.cost_of_sales?.total || 0,
                gross_profit: cData.gross_profit || 0,
                total_operating_expenses: cData.operating_expenses?.total || 0,
                operating_profit: cData.operating_profit || 0,
                total_other_income: cData.other_operating_income?.total || 0,
                total_other_expenses: cData.other_expenses?.total || 0,
                profit_before_tax: cData.profit_before_tax ?? cData.net_profit ?? 0,
                total_income_tax: cData.income_tax_expense?.total || 0,
                net_profit: cData.net_profit || 0,
                profit_margin: cData.net_margin_pct || 0
              },
              gross_revenue: mapAccounts(cData.gross_revenue?.accounts || cData.revenue?.accounts),
              discount_allowed: mapAccounts(cData.contra_revenue?.accounts || []),
              revenue: mapAccounts(cData.revenue?.accounts),
              cost_of_sales: mapAccounts(cData.cost_of_sales?.accounts),
              operating_expenses: mapAccounts(cData.operating_expenses?.accounts),
              other_income: mapAccounts(cData.other_operating_income?.accounts),
              other_expenses: mapAccounts(cData.other_expenses?.accounts),
              income_tax: mapAccounts(cData.income_tax_expense?.accounts),
              period: cData.period,
              currency: cData.display_currency || cData.currency,
              source: 'journals_unified'
            });
          } else if (reportId === 'balance_sheet') {
            const mapItems = (items) => (items || []).map(a => ({ code: a.code, name: a.name, balance: a.balance || 0 }));
            const curAssets = mapItems(cData.assets?.current);
            const ncAssets = mapItems(cData.assets?.non_current);
            const curLiab = mapItems(cData.liabilities?.current);
            const ncLiab = mapItems(cData.liabilities?.non_current);
            const eqItems = mapItems(cData.equity);
            setComparisonData({
              assets: {
                total: cData.total_assets || 0,
                current: { items: curAssets, total: curAssets.reduce((s, a) => s + (a.balance || 0), 0) },
                non_current: { items: ncAssets, total: ncAssets.reduce((s, a) => s + (a.balance || 0), 0) }
              },
              liabilities: {
                total: cData.total_liabilities || 0,
                current: { items: curLiab, total: curLiab.reduce((s, l) => s + (l.balance || 0), 0) },
                non_current: { items: ncLiab, total: ncLiab.reduce((s, l) => s + (l.balance || 0), 0) }
              },
              equity: { total: cData.total_equity || 0, items: eqItems },
              is_balanced: cData.is_balanced,
              as_of_date: cData.as_of_date,
              currency: cData.display_currency || cData.currency,
              source: 'journals_unified'
            });
          } else if (reportId === 'cash_flow') {
            setComparisonData(normalizeCashFlow(cData, (typeof cashFlowMethod === 'string' && cashFlowMethod) || 'indirect'));
          } else if (reportId === 'trial_balance') {
            setComparisonData({
              detailed: {
                accounts: cData.accounts || [],
                total_debits: cData.total_debits || 0,
                total_credits: cData.total_credits || 0,
                is_balanced: cData.is_balanced,
                period: cData.period
              },
              summary: {
                assets: cData.accounts?.filter(a => a.code?.startsWith('1')).reduce((sum, a) => sum + (a.debit || 0), 0) || 0,
                liabilities: cData.accounts?.filter(a => a.code?.startsWith('2')).reduce((sum, a) => sum + (a.credit || 0), 0) || 0,
                equity: cData.accounts?.filter(a => a.code?.startsWith('3')).reduce((sum, a) => sum + (a.credit || 0), 0) || 0,
                revenue: cData.accounts?.filter(a => a.code?.startsWith('4')).reduce((sum, a) => sum + (a.credit || 0), 0) || 0,
                expenses: cData.accounts?.filter(a => a.code?.startsWith('5') || a.code?.startsWith('6')).reduce((sum, a) => sum + (a.debit || 0), 0) || 0
              },
              source: 'journals_unified'
            });
          }
        }
      }
    } catch (error) {
      console.error('Report fetch error:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  // AI variance analysis is on-demand only (it consumes AI credit): users trigger it from the
  // "Generate Commentary" button in the AI Variance Analysis card; nothing fires automatically.
  useEffect(() => {
    if (activeReport) {
      fetchReport(activeReport);
    }
  }, [activeReport, period, comparePeriod, showComparison, customStart, customEnd, budgetCompareId]);

  const renderVariance = (current, previous, inverted = false) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / Math.abs(previous)) * 100;
    const isPositive = inverted ? change < 0 : change > 0;
    
    return (
      <span className={`flex items-center text-sm ml-2 ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
        {change > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
        {Math.abs(change).toFixed(1)}%
      </span>
    );
  };

  // Report List View
  if (!activeReport) {
    const financialReports = reports.filter(r => r.category === 'Financial Statements');
    const taxReports = reports.filter(r => r.category === 'Tax Reports');
    const managementReports = reports.filter(r => r.category === 'Management Reports');
    
    return (
      <DashboardLayout>
        <div className="p-4 lg:p-8" data-testid="reports-container">
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Financial Reports</h1>
            <p className="text-gray-600 mt-1">Auto-generated financial reports for your business</p>
          </div>

          {/* Financial Statements */}
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#283F8B]" />
              Financial Statements
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {financialReports.map((report) => (
                <Card 
                  key={report.id}
                  className="cursor-pointer hover:shadow-lg transition-all hover:border-[#283F8B] group border-2 border-transparent"
                  onClick={() => report.link ? navigate(report.link) : selectReport(report.id)}
                  data-testid={`report-${report.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                        style={{ backgroundColor: `${report.color}15` }}
                      >
                        <report.icon className="w-6 h-6" style={{ color: report.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 mb-1">{report.name}</h3>
                        <p className="text-sm text-gray-600 line-clamp-2">{report.description}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#283F8B] transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Tax Reports */}
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-600" />
              Tax Reports
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {taxReports.map((report) => (
                <Card 
                  key={report.id}
                  className="cursor-pointer hover:shadow-lg transition-all hover:border-red-500 group border-2 border-transparent"
                  onClick={() => selectReport(report.id)}
                  data-testid={`report-${report.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                        style={{ backgroundColor: `${report.color}15` }}
                      >
                        <report.icon className="w-6 h-6" style={{ color: report.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 mb-1">{report.name}</h3>
                        <p className="text-sm text-gray-600">{report.description}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Management Reports */}
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#108981]" />
              Management Reports
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {managementReports.map((report) => (
                <Card 
                  key={report.id}
                  className="cursor-pointer hover:shadow-lg transition-all hover:border-[#108981] group border-2 border-transparent"
                  onClick={() => selectReport(report.id)}
                  data-testid={`report-${report.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                        style={{ backgroundColor: `${report.color}15` }}
                      >
                        <report.icon className="w-6 h-6" style={{ color: report.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 mb-1">{report.name}</h3>
                        <p className="text-sm text-gray-600">{report.description}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#108981] transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Custom Reports (AI-Generated) */}
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#5FC6C5]" />
              Custom Reports
              <span className="text-xs font-normal text-muted-foreground ml-2">AI-generated from Ask AkountFly</span>
            </h2>
            {loadingCustomReports ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : customReports.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <FileBarChart className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <h3 className="font-medium text-foreground mb-2">No custom reports yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Ask AkountFly AI about forecasts, budgets, or product analysis to create custom reports you can save here.
                  </p>
                  <Button variant="outline" onClick={() => window.location.href = '/akountfly-ai'}>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Go to Ask AI
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {customReports.map((report) => (
                  <Card 
                    key={report.id}
                    className="hover:shadow-lg transition-all hover:border-[#5FC6C5]/30 group"
                    data-testid={`custom-report-${report.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#5FC6C5]/15 flex items-center justify-center">
                          <Sparkles className="w-6 h-6 text-[#5FC6C5]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-foreground mb-1">{report.name}</h3>
                          <p className="text-xs text-muted-foreground capitalize mb-1">
                            {report.report_type?.replace(/_/g, ' ')} • {format(new Date(report.created_at), 'MMM d, yyyy')}
                          </p>
                          <p className="text-sm text-muted-foreground line-clamp-2">{report.query}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="flex-1 h-8"
                          onClick={() => setSelectedCustomReport(report)}
                        >
                          <Eye className="w-3 h-3 mr-1" /> View
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="flex-1 h-8"
                          onClick={() => downloadCustomReport(report)}
                        >
                          <Download className="w-3 h-3 mr-1" /> Download
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => deleteCustomReport(report.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Custom Report Detail Modal */}
          {selectedCustomReport && (
            <CustomReportPreviewModal
              report={selectedCustomReport}
              onClose={() => setSelectedCustomReport(null)}
              onDownload={() => downloadCustomReport(selectedCustomReport)}
            />
          )}
        </div>
      </DashboardLayout>
    );
  }

  // Individual Report View
  const currentReport = reports.find(r => r.id === activeReport);

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-8" data-testid="report-detail-container">
        {/* Pending Schedule Warning - Shows when pending items affect reports */}
        <PendingScheduleWarning className="mb-4" />
        
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between mb-6 gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">{currentReport?.name}</h1>
            <p className="text-muted-foreground text-sm">{currentReport?.description}</p>
            {/* Sprint 2 F2.6 - freshness indicator */}
            {reportData && (
              <FreshnessChip
                className="mt-1"
                timestamp={reportData?.generated_at || reportData?.calculated_at || reportData?.timestamp || reportFetchedAt}
                testId="report-freshness-chip"
              />
            )}
          </div>
          
          {/* Actions - Export available for all reports */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <ReportExportButton
              data={reportData}
              reportType={activeReport}
              reportName={currentReport?.name || 'Financial Report'}
              period={getDateRange(period)}
              currencySymbol={getCurrencySymbol()}
              currency={displayCurrency || getBaseCurrency()}
              businessName={business?.name || business?.business_name || ''}
              captureRef={reportBodyRef}
              multiColumns={trendData ? trendData.months : (activeReport === 'pnl' && byProjectView && projectData ? projectColumns(projectData) : null)}
              cogsLabel={businessClassInfo?.cogs_label || 'Cost of Sales'}
              comparisonData={(showComparison || budgetCompareId) && comparisonData ? comparisonData : null}
              comparisonLabel={budgetCompareId
                ? `Budget${comparisonData?.budget_meta?.name ? ': ' + comparisonData.budget_meta.name : ''}`
                : (trendData ? compareOptionLabel : (activeReport === 'pnl' && byProjectView ? 'Columns by project' : (periodOptions.find((o) => o.value === comparePeriod)?.label || 'Previous period')))}
            />
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Period:</span>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="w-[160px]" data-testid="period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {periodOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {['pnl', 'balance_sheet', 'cash_flow'].includes(activeReport) && (
                <div className="flex items-center gap-2" data-testid="report-project-filter">
                  <span className="text-sm text-muted-foreground">Project:</span>
                  <ProjectSelect
                    value={projectFilterId}
                    onChange={(pid) => { setProjectFilterId(pid); fetchReport(activeReport, undefined, pid); }}
                    createdFrom="report"
                    compact
                  />
                  {activeReport === 'pnl' && (
                    <Button
                      variant={byProjectView ? 'default' : 'outline'}
                      size="sm"
                      className="h-8"
                      onClick={() => setByProjectView(v => !v)}
                      data-testid="by-project-toggle"
                    >
                      Columns by project
                    </Button>
                  )}
                </div>
              )}

              {period === 'custom' && (
                <div className="flex items-center gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Calendar className="w-4 h-4 mr-2" />
                        {customStart ? format(customStart, 'MMM d, yyyy') : 'Start'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0"><CalendarComponent mode="single" selected={customStart} onSelect={setCustomStart} /></PopoverContent>
                  </Popover>
                  <span>to</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Calendar className="w-4 h-4 mr-2" />
                        {customEnd ? format(customEnd, 'MMM d, yyyy') : 'End'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0"><CalendarComponent mode="single" selected={customEnd} onSelect={setCustomEnd} /></PopoverContent>
                  </Popover>
                </div>
              )}

              {['pnl', 'cash_flow', 'balance_sheet'].includes(activeReport) && (
                <>
                  <Separator orientation="vertical" className="h-8" />
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="compare" checked={showComparison}
                      onChange={(e) => {
                        setShowComparison(e.target.checked);
                        // Clear budget compare when period compare is engaged
                        if (e.target.checked) setBudgetCompareId(null);
                      }}
                      className="rounded" disabled={!!budgetCompareId} />
                    <label htmlFor="compare" className="text-sm">Compare with:</label>
                    <Select value={comparePeriod} onValueChange={setComparePeriod} disabled={!showComparison || !!budgetCompareId}>
                      <SelectTrigger className="w-[190px]" data-testid="compare-period-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {compareOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Phase 4 - Budget overlay selector (mutually exclusive with period) */}
                  <Separator orientation="vertical" className="h-8" />
                  <div className="flex items-center gap-2" data-testid="budget-compare-control">
                    <label className="text-sm">or Budget:</label>
                    <Select value={budgetCompareId || '__none__'}
                            onValueChange={(v) => {
                              const id = v === '__none__' ? null : v;
                              const nextB = availableBudgets.find(x => x.id === id);
                              const prevB = availableBudgets.find(x => x.id === budgetCompareId);
                              setBudgetCompareId(id);
                              // Auto-enable comparison toggle when a budget is picked.
                              if (id) setShowComparison(true);
                              // iter 1005 - project budgets compare against project-tagged actuals
                              if (nextB?.project_id) {
                                setProjectFilterId(nextB.project_id);
                              } else if (prevB?.project_id && projectFilterId === prevB.project_id) {
                                setProjectFilterId(null);
                              }
                            }}>
                      <SelectTrigger className="w-[200px]" data-testid="budget-compare-select">
                        <SelectValue placeholder="Compare with budget" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {availableBudgets.map(b => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.name} {b.scenario && b.scenario !== 'base' ? `· ${b.scenario}` : ''}{b.project_name ? ` · Project: ${b.project_name}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {/* Currency Selector for Multi-Currency Viewing */}
              {/* Note: Trial Balance excluded - it should always show base currency */}
              {['pnl', 'cash_flow', 'balance_sheet', 'equity'].includes(activeReport) && (
                <>
                  <Separator orientation="vertical" className="h-8" />
                  <CurrencySelector
                    value={displayCurrency || getBaseCurrency()}
                    onChange={(currency) => {
                      // `null` means "back to base currency" (no display_currency query param).
                      const newDisplayCurrency = currency === getBaseCurrency() ? null : currency;
                      setDisplayCurrency(newDisplayCurrency);
                      // Refetch IMMEDIATELY with the new currency. We pass the override
                      // to avoid the React-state-flush race that previously caused the
                      // base-currency switch to only update the symbol, not the numbers.
                      fetchReport(activeReport, newDisplayCurrency);
                    }}
                    baseCurrency={getBaseCurrency()}
                    showRate={true}
                  />
                  {convertingCurrency && (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  )}
                </>
              )}


              <Button variant="outline" size="sm" onClick={() => fetchReport(activeReport)} className="ml-auto">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            
            {/* GL Code Toggle - shown in detailed view */}
            {viewMode === 'detailed' && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={showGlCodes} 
                    onChange={(e) => setShowGlCodes(e.target.checked)}
                    className="rounded"
                  />
                  Show GL Codes
                </label>
                <span className="text-xs text-muted-foreground ml-2">
                  Click any row to drill down to transactions
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {projectFilterId && ['pnl', 'balance_sheet', 'cash_flow'].includes(activeReport) && (
          <div className="mb-4 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 text-sm text-blue-800" data-testid="project-filter-banner">
            <span>
              Showing <strong>project-tagged transactions only</strong>. Untagged transactions are excluded: clear the project filter to see everything.
              {activeReport !== 'pnl' && ' Note: some balances (bank accounts, taxes, equity) belong to the whole business, so this view may not balance on its own.'}
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#283F8B]" />
          </div>
        ) : (
          <div ref={reportBodyRef} data-testid="report-body">
            {/* View Mode Toggle for applicable reports */}
            {['pnl', 'balance_sheet', 'cash_flow', 'equity'].includes(activeReport) && (
              <Tabs value={viewMode} onValueChange={setViewMode} className="mb-6" data-pdf-hide="">
                <TabsList>
                  <TabsTrigger value="summary" data-testid="cashflow-tab-summary-view">Summary View</TabsTrigger>
                  <TabsTrigger value="detailed" data-testid="cashflow-tab-detailed-view">Detailed View</TabsTrigger>
                </TabsList>
              </Tabs>
            )}

            {/* P&L Report */}
            {activeReport === 'pnl' && byProjectView && (
              <ProjectColumnsPnl
                startDate={getDateRange(period)?.start}
                endDate={getDateRange(period)?.end}
                displayCurrency={displayCurrency || null}
                formatAmount={(amount) => formatAmount(amount, displayCurrency)}
                showGlCodes={showGlCodes}
                openDrilldown={openDrilldown}
                cogsLabel={businessClassInfo?.cogs_label || 'Cost of Sales'}
                netLabel={regimeLabels.net_result || 'Net Profit'}
                onData={setProjectData}
              />
            )}

            {activeReport === 'pnl' && !byProjectView && reportData && trendData && (
              <>
                <MultiColumnStatement
                  reportType="pnl"
                  columns={trendData.months}
                  formatAmount={(amount) => formatAmount(amount, displayCurrency)}
                  showGlCodes={showGlCodes}
                  openDrilldown={openDrilldown}
                  cogsLabel={businessClassInfo?.cogs_label || 'Cost of Sales'}
                  netLabel={regimeLabels.net_result || 'Net Profit'}
                  title={`${currentReport?.name || 'Profit & Loss'} · ${compareOptionLabel}`}
                  description="One column per month with the full account breakdown; the Total column adds the months together."
                  testId="pnl-trend-view"
                />
                <div className="mt-6">
                  <AICommentary
                    currentData={trendAiPair(trendData.months)?.current}
                    comparisonData={trendAiPair(trendData.months)?.comparison}
                    reportType="pnl"
                    currencySymbol={getCurrencySymbol(displayCurrency)}
                    projectId={projectFilterId || null}
                    onGenerate={(data) => setAiCommentary(data)}
                  />
                </div>
              </>
            )}

            {activeReport === 'pnl' && !byProjectView && reportData && !trendData && (
              <>
                <ProfitLossReport
                  reportData={reportData}
                  comparisonData={comparisonData}
                  showComparison={showComparison}
                  viewMode={viewMode}
                  formatAmount={(amount) => formatAmount(amount, displayCurrency)}
                  renderVariance={renderVariance}
                  showGlCodes={showGlCodes}
                  openDrilldown={openDrilldown}
                  aiCommentary={aiCommentary}
                  loadingAi={loadingAi}
                  getCurrencySymbol={() => getCurrencySymbol(displayCurrency)}
                  cogsLabel={businessClassInfo?.cogs_label || 'Cost of Sales'}
                  cogsCoverage={cogsCoverage}
                />
                
                {/* AI Variance Analysis - P&L */}
                {showComparison && (
                  <div className="mt-6">
                    <AICommentary
                      currentData={reportData}
                      comparisonData={comparisonData}
                      reportType="pnl"
                      currencySymbol={getCurrencySymbol(displayCurrency)}
                      budgetId={budgetCompareId || null}
                      projectId={projectFilterId || null}
                      onGenerate={(data) => setAiCommentary(data)}
                    />
                  </div>
                )}
              </>
            )}

            {/* Balance Sheet */}
            {activeReport === 'balance_sheet' && reportData && trendData && (
              <>
                <MultiColumnStatement
                  reportType="balance_sheet"
                  columns={trendData.months}
                  formatAmount={(amount) => formatAmount(amount, displayCurrency)}
                  showGlCodes={showGlCodes}
                  openDrilldown={openDrilldown}
                  title={`${currentReport?.name || 'Balance Sheet'} · ${compareOptionLabel}`}
                  description="Month-end balances side by side, with the full account breakdown."
                  testId="bs-trend-view"
                />
                <div className="mt-6">
                  <AICommentary
                    currentData={trendAiPair(trendData.months)?.current}
                    comparisonData={trendAiPair(trendData.months)?.comparison}
                    reportType="balance_sheet"
                    currencySymbol={getCurrencySymbol(displayCurrency)}
                    projectId={projectFilterId || null}
                  />
                </div>
              </>
            )}

            {activeReport === 'balance_sheet' && reportData && !trendData && (
              <>
                <BalanceSheetReport
                  reportData={reportData}
                  comparisonData={comparisonData}
                  showComparison={showComparison}
                  viewMode={viewMode}
                  formatAmount={(amount) => formatAmount(amount, displayCurrency)}
                  renderVariance={renderVariance}
                  showGlCodes={showGlCodes}
                  openDrilldown={openDrilldown}
                />
                
                {/* AI Variance Analysis - Balance Sheet */}
                {showComparison && (
                  <div className="mt-6">
                    <AICommentary
                      currentData={reportData}
                      comparisonData={comparisonData}
                      reportType="balance_sheet"
                      currencySymbol={getCurrencySymbol(displayCurrency)}
                      budgetId={budgetCompareId || null}
                      projectId={projectFilterId || null}
                    />
                  </div>
                )}
              </>
            )}

            {/* Cash Flow */}
            {activeReport === 'cash_flow' && reportData && (
              <>
                {/* Phase 1-4 (Feb 2026) - Method toggle (Indirect default).
                    Indirect starts from Net Profit (P&L SSOT) and
                    reconciles via non-cash adjustments + working capital
                    deltas (Balance Sheet SSOT). Direct shows totals only
                    - no per-tx detail. */}
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4 p-3 rounded-lg border bg-gradient-to-r from-slate-50 to-card" data-testid="cashflow-method-toggle">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-muted-foreground">Method:</span>
                    <div className="inline-flex rounded-md border bg-card p-0.5">
                      <button
                        onClick={() => setCashFlowMethod('indirect')}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${cashFlowMethod === 'indirect' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted'}`}
                        data-testid="cashflow-method-indirect"
                      >
                        Indirect
                        <span className="ml-1 text-[10px] opacity-70">(IAS 7)</span>
                      </button>
                      <button
                        onClick={() => setCashFlowMethod('direct')}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${cashFlowMethod === 'direct' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted'}`}
                        data-testid="cashflow-method-direct"
                      >
                        Direct
                      </button>
                    </div>
                  </div>
                  {reportData?.reconciliation && (
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-md text-xs ${reportData.reconciliation.is_reconciled ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`} data-testid="cashflow-reconciliation-status">
                      {reportData.reconciliation.is_reconciled ? (
                        <>✅ Reconciled to Balance Sheet bank movement</>
                      ) : (
                        <>⚠️ Variance: {formatAmount(reportData.reconciliation.variance, displayCurrency)}: investigate</>
                      )}
                    </div>
                  )}
                </div>

                {trendData ? (
                  <>
                    <MultiColumnStatement
                      reportType="cash_flow"
                      columns={trendData.months}
                      formatAmount={(amount) => formatAmount(amount, displayCurrency)}
                      title={`Cash Flow Statement · ${compareOptionLabel}`}
                      description="Monthly cash movement side by side; the Total column adds the months together (opening/closing cash are not summed)."
                      testId="cf-trend-view"
                    />
                    <div className="mt-6">
                      <AICommentary
                        currentData={trendAiPair(trendData.months)?.current}
                        comparisonData={trendAiPair(trendData.months)?.comparison}
                        reportType="cash_flow"
                        currencySymbol={getCurrencySymbol(displayCurrency)}
                        projectId={projectFilterId || null}
                      />
                    </div>
                  </>
                ) : (
                <>
                <CashFlowReport
                  reportData={reportData}
                  comparisonData={comparisonData}
                  showComparison={showComparison}
                  viewMode={viewMode}
                  formatAmount={(amount) => formatAmount(amount, displayCurrency)}
                  renderVariance={renderVariance}
                  showGlCodes={showGlCodes}
                  openDrilldown={openDrilldown}
                  fetchLineItems={fetchCashFlowLineItems}
                />
                
                {/* AI Variance Analysis - Cash Flow */}
                {showComparison && (
                  <div className="mt-6">
                    <AICommentary
                      currentData={reportData}
                      comparisonData={comparisonData}
                      reportType="cash_flow"
                      currencySymbol={getCurrencySymbol(displayCurrency)}
                      budgetId={budgetCompareId || null}
                      projectId={projectFilterId || null}
                    />
                  </div>
                )}
                </>
                )}
              </>
            )}

            {/* Statement of Changes in Equity */}
            {activeReport === 'equity' && reportData && (
              <StatementOfChangesInEquity data={reportData} formatAmount={formatAmount} />
            )}

            {/* Trial Balance */}
            {activeReport === 'trial_balance' && reportData && (
              <TrialBalanceReport
                reportData={reportData.detailed || reportData}
                viewMode={trialBalanceViewMode}
                formatAmount={formatAmount}
                showGlCodes={showGlCodes}
                showZeroBalances={showZeroBalances}
                setShowZeroBalances={(val) => {
                  setShowZeroBalances(val);
                  fetchReport('trial_balance');
                }}
                trialBalanceFilter={trialBalanceFilter}
                setTrialBalanceFilter={setTrialBalanceFilter}
                openDrilldown={openDrilldown}
              />
            )}

            {/* Aged Receivables */}
            {activeReport === 'aged_receivables' && reportData && (
              <AgedReceivablesReport
                reportData={reportData}
                formatAmount={formatAmount}
                getCurrencySymbol={getCurrencySymbol}
              />
            )}

            {/* Aged Payables */}
            {/* VAT Report - Output Tax (Sales) and Input Tax (Purchases) */}
            {activeReport === 'tax_report' && reportData && (
              <div className="space-y-6" data-testid="vat-report">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">Output VAT (Collected)</p>
                      <p className="text-2xl font-bold text-red-600">{formatAmount(reportData.output_tax?.total || reportData.tax_collected?.total || 0)}</p>
                      <p className="text-xs text-muted-foreground">{reportData.output_tax?.count || 0} invoice transactions</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">Input VAT (Recoverable)</p>
                      <p className="text-2xl font-bold text-green-600">{formatAmount(reportData.input_tax?.total || reportData.tax_paid?.amount || 0)}</p>
                      <p className="text-xs text-muted-foreground">{reportData.input_tax?.count || 0} bill transactions</p>
                    </CardContent>
                  </Card>
                  <Card className={reportData.net_tax_liability >= 0 ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'}>
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">
                        {reportData.net_tax_liability >= 0 ? 'Net VAT Payable' : 'Net VAT Refundable'}
                      </p>
                      <p className={`text-2xl font-bold ${reportData.net_tax_liability >= 0 ? 'text-orange-600' : 'text-blue-600'}`}>
                        {formatAmount(Math.abs(reportData.net_tax_liability || 0))}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {reportData.net_tax_liability >= 0 ? 'Payable to tax authority' : 'Reclaimable from tax authority'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Output Tax Transactions */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        Output Tax (Sales)
                      </CardTitle>
                      <Badge variant="outline" className="bg-red-50 text-red-700">
                        {formatAmount(reportData.output_tax?.total || 0)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {(reportData.output_tax?.transactions || []).length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="text-xs">
                            <TableHead>Date</TableHead>
                            <TableHead>Reference</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Account</TableHead>
                            <TableHead className="text-right">Sales Amount</TableHead>
                            <TableHead className="text-right">VAT Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(reportData.output_tax?.transactions || []).map((t, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-sm">{t.date}</TableCell>
                              <TableCell><Badge variant="outline" className="text-xs">{t.reference}</Badge></TableCell>
                              <TableCell className="text-sm max-w-xs truncate">{t.description}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{t.account_code}</TableCell>
                              <TableCell className="text-right text-sm">{t.sales_amount ? formatAmount(t.sales_amount) : '-'}</TableCell>
                              <TableCell className="text-right font-medium text-red-600">{formatAmount(t.amount)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-red-50/50 font-medium">
                            <TableCell colSpan={4}>Total Output Tax</TableCell>
                            <TableCell className="text-right text-sm">
                              {formatAmount((reportData.output_tax?.transactions || []).reduce((s, t) => s + (t.sales_amount || 0), 0))}
                            </TableCell>
                            <TableCell className="text-right font-bold text-red-600">{formatAmount(reportData.output_tax?.total || 0)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-4 text-muted-foreground">No output tax transactions found</p>
                    )}
                  </CardContent>
                </Card>

                {/* Input Tax Transactions */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        Input Tax (Purchases)
                      </CardTitle>
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        {formatAmount(reportData.input_tax?.total || 0)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {(reportData.input_tax?.transactions || []).length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="text-xs">
                            <TableHead>Date</TableHead>
                            <TableHead>Reference</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Account</TableHead>
                            <TableHead className="text-right">Purchase Amount</TableHead>
                            <TableHead className="text-right">VAT Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(reportData.input_tax?.transactions || []).map((t, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-sm">{t.date}</TableCell>
                              <TableCell><Badge variant="outline" className="text-xs">{t.reference}</Badge></TableCell>
                              <TableCell className="text-sm max-w-xs truncate">{t.description}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{t.account_code}</TableCell>
                              <TableCell className="text-right text-sm">{t.purchase_amount ? formatAmount(t.purchase_amount) : '-'}</TableCell>
                              <TableCell className="text-right font-medium text-green-600">{formatAmount(t.amount)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-green-50/50 font-medium">
                            <TableCell colSpan={4}>Total Input Tax</TableCell>
                            <TableCell className="text-right text-sm">
                              {formatAmount((reportData.input_tax?.transactions || []).reduce((s, t) => s + (t.purchase_amount || 0), 0))}
                            </TableCell>
                            <TableCell className="text-right font-bold text-green-600">{formatAmount(reportData.input_tax?.total || 0)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-4 text-muted-foreground">No input tax transactions found</p>
                    )}
                  </CardContent>
                </Card>

                {/* Period Info */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Report Period</p>
                        <p className="font-medium">{reportData.period?.start} to {reportData.period?.end}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Country</p>
                        <p className="font-medium">{reportData.country}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* VAT Return */}
            {activeReport === 'vat_return' && reportData && (
              <div className="space-y-6" data-testid="vat-return">
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className={reportData.summary?.vat_payable > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}>
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">
                        {reportData.summary?.vat_payable > 0 ? `${reportData.return_form?.tax_name || 'VAT'} payable` : `${reportData.return_form?.tax_name || 'VAT'} refund / credit`}
                      </p>
                      <p className={`text-3xl font-bold ${reportData.summary?.vat_payable > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatAmount(reportData.summary?.vat_payable > 0 ? reportData.summary.vat_payable : reportData.summary?.vat_refund || 0)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">{reportData.return_form?.tax_name || 'VAT'} rate ({reportData.rates?.source === 'tax_pack' ? 'statutory pack' : reportData.rates?.source === 'state_table' ? `${reportData.rates.home_state} state` : 'default'})</p>
                      <p className="text-3xl font-bold text-foreground">{reportData.return_form?.rates?.standard_rate ?? reportData.tax_rate}%</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Country-specific return form (boxes/labels/authority from sales_tax_return_templates.py) */}
                <SalesTaxReturnForm form={reportData.return_form} formatAmount={formatAmount} startDate={reportData.period?.start} endDate={reportData.period?.end} />
              </div>
            )}

            {activeReport === 'aged_payables' && reportData && (
              <AgedPayablesReport
                reportData={reportData}
                formatAmount={formatAmount}
                getCurrencySymbol={getCurrencySymbol}
              />
            )}

            {activeReport === 'wht_remittance' && (
              <WHTRemittanceReport />
            )}

            {activeReport === 'fx_gainloss' && (
              <FXGainLossReport
                baseCurrency={business?.currency || 'NGN'}
                dateRange={getDateRange(period)}
              />
            )}
          </div>
        )}
      </div>
      
      {/* Universal GL Drilldown Modal */}
      <GLDrilldownModal
        open={drilldownOpen}
        onOpenChange={handleDrilldownOpenChange}
        glCode={drilldownGlCode}
        accountId={drilldownAccountId}
        accountName={drilldownAccountName}
        startDate={(() => {
          const range = getDateRange(period);
          return range?.start;
        })()}
        endDate={(() => {
          const range = getDateRange(period);
          return range?.end;
        })()}
        activeReport={activeReport}
      />
    </DashboardLayout>
  );
};

export default ReportsPage;
